import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Plus, Search, Trash2, Edit2, X, History, Camera, Loader2, Check, Square, CheckSquare } from 'lucide-react';

const CATEGORIES = [
  { value: 'Proteinas', label: 'Proteinas' }, { value: 'Vegetales', label: 'Vegetales' },
  { value: 'Frutas', label: 'Frutas' }, { value: 'Lacteos', label: 'Lacteos' },
  { value: 'Granos', label: 'Granos' }, { value: 'Especias', label: 'Especias' },
  { value: 'Aceites', label: 'Aceites' }, { value: 'Otros', label: 'Otros' },
];
const UNITS = [
  { value: 'kg', label: 'kg' }, { value: 'g', label: 'g' }, { value: 'lb', label: 'lb' },
  { value: 'oz', label: 'oz' }, { value: 'L', label: 'L' }, { value: 'ml', label: 'ml' },
  { value: 'unidad', label: 'unidad' }, { value: 'docena', label: 'docena' },
];

export default function Ingredients() {
  const { axiosAuth, user, permissions } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState('');
  const [history, setHistory] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', category: '', supplier: '', price: '', unit: 'kg', waste_percentage: '', min_stock: '', notes: '' });
  const currency = user?.currency || 'USD';

  const load = async () => {
    try { const res = await axiosAuth().get('/ingredients'); setItems(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ name: '', category: '', supplier: '', price: '', unit: 'kg', waste_percentage: '', min_stock: '', notes: '' }); setEditItem(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0, waste_percentage: parseFloat(form.waste_percentage) || 0, min_stock: parseFloat(form.min_stock) || 0 };
    try {
      if (editItem) await axiosAuth().put(`/ingredients/${editItem.id}`, payload);
      else await axiosAuth().post('/ingredients', payload);
      resetForm(); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Eliminar ingrediente?')) return; await axiosAuth().delete(`/ingredients/${id}`); load(); };
  const handleEdit = (item) => {
    setForm({ name: item.name, category: item.category || '', supplier: item.supplier || '', price: String(item.price), unit: item.unit, waste_percentage: String(item.waste_percentage || ''), min_stock: String(item.min_stock || ''), notes: item.notes || '' });
    setEditItem(item); setShowForm(true);
  };
  const showHistory = async (id) => { const res = await axiosAuth().get(`/ingredients/${id}/price-history`); setHistory({ id, data: res.data }); };

  const handleInvoiceScan = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setScanning(true); setScanResults(null); setSelectedItems({});
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await axiosAuth().post('/ingredients/scan-invoice', { image: reader.result });
        setScanResults(res.data);
        const preSelected = {};
        res.data.items?.forEach((item, idx) => { if (item.confidence >= 0.7) preSelected[idx] = true; });
        setSelectedItems(preSelected);
      } catch { alert('Error al escanear factura'); }
      finally { setScanning(false); }
    };
    reader.readAsDataURL(file);
  };

  const toggleItemSelection = (idx) => setSelectedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  const selectAllItems = () => { const all = {}; scanResults?.items?.forEach((_, idx) => { all[idx] = true; }); setSelectedItems(all); };
  const deselectAllItems = () => setSelectedItems({});

  const importSelectedItems = async () => {
    const itemsToImport = scanResults?.items?.filter((_, idx) => selectedItems[idx]) || [];
    if (itemsToImport.length === 0) { alert('Selecciona al menos un item'); return; }
    setImporting(true); let imported = 0;
    for (const item of itemsToImport) {
      try { await axiosAuth().post('/ingredients', { name: item.name || '', category: '', supplier: scanResults?.supplier || '', price: parseFloat(item.price) || 0, unit: item.unit || 'kg', waste_percentage: 0, min_stock: 0, notes: `Importado de factura` }); imported++; }
      catch { console.error('Error importing:', item.name); }
    }
    setImporting(false); setScanResults(null); setSelectedItems({}); load();
    alert(`${imported} ingrediente(s) importado(s)`);
  };

  const importSingleItem = (item) => {
    setForm({ name: item.name || '', category: '', supplier: scanResults?.supplier || '', price: String(item.price || ''), unit: item.unit || 'kg', waste_percentage: '', min_stock: '', notes: 'Importado de factura' });
    setShowForm(true);
  };

  const filtered = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || i.category?.toLowerCase().includes(search.toLowerCase()));
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ingredients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Ingredientes</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Base de Datos</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="file" ref={fileInputRef} accept="image/*" onChange={handleInvoiceScan} className="hidden" />
          <button onClick={() => fileInputRef.current?.click()} disabled={scanning} data-testid="scan-invoice-btn"
            className="flex items-center gap-1.5 px-3 py-2 border border-gold/40 text-gold text-[10px] sm:text-xs font-bold uppercase tracking-widest hover:bg-gold/10 transition-colors disabled:opacity-50">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            <span className="hidden sm:inline">{scanning ? 'Escaneando...' : 'Escanear Factura'}</span>
            <span className="sm:hidden">{scanning ? '...' : 'Escanear'}</span>
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-ingredient-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-gold text-black text-[10px] sm:text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors">
            <Plus size={14} /> Agregar
          </button>
        </div>
      </div>

      {/* Scan Results */}
      {scanResults && scanResults.items?.length > 0 && (
        <div className="bg-gold/5 border border-gold/20 p-6" data-testid="scan-results">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gold text-sm font-semibold">Factura Escaneada</p>
            <button onClick={() => { setScanResults(null); setSelectedItems({}); }} className="text-white/30 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
            <div className="flex items-center gap-4">
              <button onClick={selectAllItems} className="text-xs text-gold hover:underline">Seleccionar todos</button>
              <button onClick={deselectAllItems} className="text-xs text-white/40 hover:text-white">Deseleccionar</button>
            </div>
            <span className="text-xs text-white/60">{selectedCount} de {scanResults.items.length}</span>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {scanResults.items.map((item, i) => (
              <div key={i} onClick={() => toggleItemSelection(i)}
                className={`flex items-center gap-3 py-3 px-4 border cursor-pointer transition-all ${selectedItems[i] ? 'border-gold/50 bg-gold/10' : 'border-white/10 hover:border-white/20'}`}
                data-testid={`scan-item-${i}`}>
                <div>{selectedItems[i] ? <CheckSquare size={20} className="text-gold" /> : <Square size={20} className="text-white/30" />}</div>
                <div className="flex-1"><span className="text-sm">{item.name}</span><div className="text-xs text-white/40">{item.quantity && `${item.quantity} `}{item.unit} - {currency} {item.price}</div></div>
                <button onClick={(e) => { e.stopPropagation(); importSingleItem(item); }} className="px-3 py-1.5 text-xs border border-white/20 text-white/60 hover:border-gold hover:text-gold">Editar</button>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-white/10">
            <button onClick={importSelectedItems} disabled={selectedCount === 0 || importing} data-testid="import-selected-btn"
              className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 disabled:opacity-50 flex items-center justify-center gap-2">
              {importing ? <><Loader2 size={14} className="animate-spin" /> Importando...</> : <><Check size={14} /> Importar {selectedCount} Seleccionados</>}
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-3 text-white/30" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar ingrediente..." data-testid="ingredient-search"
          className="w-full bg-[#0A0A0A] border border-white/10 py-3 pl-10 pr-4 text-sm focus:border-gold focus:outline-none" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="ingredients-table">
          <thead><tr className="border-b border-white/10">
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4">Nombre</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4 hidden sm:table-cell">Categoria</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4 hidden md:table-cell">Proveedor</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4">Precio</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4 hidden sm:table-cell">Merma</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4">Costo Real</th>
            <th className="text-center text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4">Acciones</th>
          </tr></thead>
          <tbody>
            {filtered.map((item, i) => (
              <tr key={item.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`ingredient-row-${i}`}>
                <td className="py-3 px-2 sm:px-4 text-sm font-medium">{item.name}</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-white/60 hidden sm:table-cell">{item.category}</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-white/60 hidden md:table-cell">{item.supplier}</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-right text-gold font-mono">{currency} {item.price?.toFixed(2)}</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-right text-white/60 hidden sm:table-cell">{item.waste_percentage}%</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-right text-gold font-mono">{currency} {item.real_cost?.toFixed(2)}</td>
                <td className="py-3 px-2 sm:px-4"><div className="flex items-center justify-center gap-1 sm:gap-2">
                  {permissions.can_edit && <button onClick={() => handleEdit(item)} data-testid={`edit-ingredient-${i}`} className="p-1 text-white/30 hover:text-gold"><Edit2 size={14} /></button>}
                  <button onClick={() => showHistory(item.id)} data-testid={`history-ingredient-${i}`} className="p-1 text-white/30 hover:text-blue-400"><History size={14} /></button>
                  {permissions.can_delete && <button onClick={() => handleDelete(item.id)} data-testid={`delete-ingredient-${i}`} className="p-1 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center text-white/30 py-12 text-sm">No hay ingredientes</p>}
      </div>

      {/* Ingredient Form - inline */}
      {showForm && (
        <div data-testid="ingredient-form-modal">
          <div className="flex items-center gap-3 mb-6">
            <button type="button" onClick={resetForm} data-testid="close-ingredient-form" className="text-white/40 hover:text-white text-sm flex items-center gap-1"><X size={14} /> Volver</button>
            <h3 className="font-heading text-xl font-bold">{editItem ? 'Editar' : 'Nuevo'} Ingrediente</h3>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Nombre *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="ingredient-name-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Categoria</label>
                  <MobileDropdown options={CATEGORIES} value={form.category} onChange={v => setForm(p => ({ ...p, category: v }))} testId="ingredient-category-select" placeholder="Seleccionar..." />
                </div>
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Proveedor</label>
                  <input value={form.supplier} onChange={e => setForm(p => ({ ...p, supplier: e.target.value }))} data-testid="ingredient-supplier-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Precio ({currency})</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} required data-testid="ingredient-price-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Unidad</label>
                  <MobileDropdown options={UNITS} value={form.unit} onChange={v => setForm(p => ({ ...p, unit: v }))} testId="ingredient-unit-select" placeholder="Unidad" />
                </div>
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Merma %</label>
                  <input type="number" step="0.1" min="0" max="99" value={form.waste_percentage} onChange={e => setForm(p => ({ ...p, waste_percentage: e.target.value }))} data-testid="ingredient-waste-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Stock minimo</label>
                <input type="number" step="0.1" value={form.min_stock} onChange={e => setForm(p => ({ ...p, min_stock: e.target.value }))} data-testid="ingredient-minstock-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              <button type="submit" data-testid="save-ingredient-btn" className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors mt-4">
                {editItem ? 'Actualizar' : 'Guardar'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Price History Modal */}
      {history && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85" data-testid="price-history-modal">
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">Historial de Precios</h3>
              <button onClick={() => setHistory(null)} className="text-white/30 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {history.data.map((h, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs text-white/40">{h.date?.slice(0, 10)}</span>
                  <span className="text-sm text-gold font-mono">{currency} {h.price?.toFixed(2)}</span>
                  <span className="text-xs text-white/40">{h.supplier}</span>
                </div>
              ))}
              {history.data.length === 0 && <p className="text-center text-white/30 text-sm">Sin historial</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
